<template>
  <v-container>
    <!-- page header -->
    <v-row>
      <v-col cols="12" sm="6">
        <h1 class="display-1">Edit User</h1>
        <p class="body-1">{{ userAccount.email }}</p>
      </v-col>
      <v-col cols="12" md="6" align="end">
        <v-btn color="primary" depressed to="/users" exact>
          Back to Users</v-btn
        >
      </v-col>
    </v-row>
    <!-- page header -->

    <!-- row 1 -->
    <v-row>
      <v-col cols="12">
        <v-card outlined :loading="loading">
          <v-card-text class="text--primary">
            <v-row>
              <!-- user account info -->
              <v-col cols="12" md="4" v-if="userAccount">
                <h3 class="mb-3 title">User Information</h3>
                <ul>
                  <li>
                    Name: {{ userAccount.firstName }} {{ userAccount.lastName }}
                  </li>
                  <li v-if="userAccount.email">
                    <v-btn
                      small
                      text
                      color="primary"
                      class="px-0"
                      @click="copyText(userAccount.email)"
                    >
                      Email: {{ userAccount.email }}
                      <v-icon right small>mdi-content-copy</v-icon></v-btn
                    >

                    <v-btn small text color="primary" @click="dialog = true"
                      ><v-icon>mdi-square-edit-outline</v-icon></v-btn
                    >
                  </li>

                  <!-- edit email pop up -->
                  <div v-if="dialog">
                    <v-dialog
                      v-model="dialog"
                      width="30%"
                      transition="dialog-bottom-transition"
                    >
                      <v-card tile>
                        <!-- dialog toolbar -->
                        <v-toolbar
                          dark
                          color="primary"
                          dense
                          flat
                          style="
                            position: sticky;
                            top: 0;
                            width: 100%;
                            z-index: 500;
                          "
                        >
                          <v-toolbar-title>
                            <strong>Update Email Address</strong>
                            <span class="ml-6"> </span>
                          </v-toolbar-title>
                          <v-spacer />
                          <v-toolbar-items>
                            <v-btn
                              depressed
                              color="primary"
                              dark
                              :loading="loading"
                              :disabled="loading"
                              @click="dialog = false"
                            >
                              <v-icon>mdi-close</v-icon>
                            </v-btn>
                          </v-toolbar-items>
                        </v-toolbar>
                        <v-card-text class="text--primary mt-6">
                          <!-- form -->
                          <v-form ref="form" @submit.prevent="saveEmail">
                            <v-row style="justify-content: center">
                              <v-col cols="9">
                                <v-text-field
                                  v-model="email"
                                  :loading="loading"
                                  :disabled="loading"
                                  label="Enter Email Address"
                                  :rules="emailRules"
                                  required
                                  hide-details="auto"
                                ></v-text-field> </v-col
                            ></v-row>
                            <v-row style="justify-content: center">
                              <v-col cols="3">
                                <v-btn
                                  depressed
                                  :loading="loading"
                                  color="primary"
                                  :disabled="!isEmailValid || emailSaving"
                                  style="width: inherit"
                                  @click="saveEmail"
                                >
                                  Submit
                                </v-btn>
                              </v-col></v-row
                            ></v-form
                          ></v-card-text
                        ></v-card
                      ></v-dialog
                    >
                  </div>

                  <!-- edit email pop up -->

                  <li v-if="userAccount.secondaryEmail">
                    Secondary Email: {{ userAccount.secondaryEmail }}
                  </li>
                  <li>
                    <v-btn
                      small
                      text
                      color="primary"
                      class="px-0"
                      @click="copyText(userAccount.packageID)"
                    >
                      PUID: {{ userAccount.packageID }}
                      <v-icon right small>mdi-content-copy</v-icon></v-btn
                    >
                  </li>
                  <li>
                    Date Registered:
                    {{ formatDate(userAccount.dateRegistered) }}
                  </li>
                  <li>
                    Date Last Signed In:
                    {{ formatDate(userAccount.lastSignedIn) }}
                  </li>
                  <li>Package Code: {{ eventReportWinner.packageCode }}</li>
                  <li>Destination: {{ eventReportWinner.destination }}</li>
                  <li>
                    Number of Nights: {{ eventReportWinner.numberNights }}
                  </li>
                  <li>Unit Type: {{ eventReportWinner.unitType }}</li>
                  <li v-if="eventReport.eventReportNumber">
                    <v-btn
                      small
                      text
                      color="primary"
                      class="px-0"
                      :to="`/event-reports?q=${eventReport.id}`"
                    >
                      Event Report #: {{ eventReport.eventReportNumber }}</v-btn
                    >
                  </li>
                  <li>Event Name: {{ eventReport.eventName }}</li>
                  <li>Event Date: {{ formatDate(eventReport.eventDate) }}</li>
                  <li v-if="eventReportWinner.travelByDate">
                    <v-btn
                      small
                      text
                      color="primary"
                      class="px-0"
                      @click="travelByDateDialog = true"
                    >
                      Travel By Date:
                      {{ formatDate(eventReportWinner.travelByDate) }}</v-btn
                    >
                  </li>
                  <li v-if="userAccount.mailingAddress">
                    Address 1: {{ userAccount.mailingAddress.address1 }}
                  </li>
                  <li v-if="userAccount.mailingAddress">
                    Address 2: {{ userAccount.mailingAddress.address2 }}
                  </li>
                  <li v-if="userAccount.mailingAddress">
                    City: {{ userAccount.mailingAddress.city }}
                  </li>
                  <li v-if="userAccount.mailingAddress">
                    State: {{ userAccount.mailingAddress.state }}
                  </li>
                  <li v-if="userAccount.mailingAddress">
                    Zip Code: {{ userAccount.mailingAddress.zipCode }}
                  </li>
                  <li v-if="userAccount.mailingAddress">
                    Country: {{ userAccount.mailingAddress.country }}
                  </li>
                  <li v-if="userAccount.phone">
                    Phone: {{ formatPhone(userAccount.phone) }}
                  </li>

                  <v-subheader v-if="eventReport.auctioneerUsed"
                    ><strong>Auctioneer Info:</strong></v-subheader
                  >
                  <li
                    v-if="
                      eventReport.auctioneerUsed && eventReport.auctioneerName
                    "
                  >
                    Name: {{ eventReport.auctioneerName }}
                  </li>
                  <li
                    v-if="
                      eventReport.auctioneerUsed &&
                        eventReport.auctioneerCompany
                    "
                  >
                    Company: {{ eventReport.auctioneerCompany }}
                  </li>
                  <li
                    v-if="
                      eventReport.auctioneerUsed && eventReport.auctioneerEmail
                    "
                  >
                    Email: {{ eventReport.auctioneerEmail }}
                  </li>
                </ul>
              </v-col>

              <!-- access controls -->
              <v-col cols="12" md="4">
                <h3 class="mb-3 title">Access Control</h3>
                <v-switch
                  dense
                  hide-details
                  inset
                  v-model="userAccount.canAccess"
                  label="Can Access Dashboard"
                ></v-switch>
                <v-switch
                  dense
                  hide-details
                  inset
                  v-model="userAccount.canBook"
                  label="Can Book"
                ></v-switch>
                <v-switch
                  dense
                  hide-details
                  inset
                  v-model="userAccount.canBrowseInventory"
                  label="Can Browse Inventory"
                ></v-switch>
                <v-switch
                  dense
                  hide-details
                  inset
                  v-model="userAccount.canSendMessages"
                  label="Can Send Messages"
                ></v-switch>
                <v-btn
                  color="primary"
                  @click="updateAccessControls"
                  depressed
                  class="mt-8"
                  :loading="loading"
                  :disabled="loading"
                  >Update Access Controls</v-btn
                >
              </v-col>

              <!-- Right lane: PUID Ownership (mgmt only) + Escalation (stacked) -->
              <v-col cols="12" md="4">
                <!-- Management-only: PUID Ownership -->
                <div v-if="canManagePuid" class="mt-8">
                  <v-divider class="mb-4" />

                  <h3 class="mb-3 title">PUID Ownership</h3>

                  <p class="mb-2">
                    <strong>Assigned To: </strong>
                    <span v-if="userAccount.assignedToName">{{
                      userAccount.assignedToName
                    }}</span>
                    <span v-else-if="userAccount.assignedToUid">{{
                      userAccount.assignedToUid
                    }}</span>
                    <span v-else>Unassigned</span>
                  </p>

                  <p class="mb-4">
                    <strong>Assumed By: </strong>
                    <span v-if="userAccount.assumedByName">{{
                      userAccount.assumedByName
                    }}</span>
                    <span v-else-if="userAccount.assumedByUid">{{
                      userAccount.assumedByUid
                    }}</span>
                    <span v-else>—</span>
                  </p>

                  <v-row dense>
                    <v-col cols="12">
                      <v-btn
                        color="primary"
                        depressed
                        block
                        class="mb-2"
                        :loading="loading"
                        :disabled="loading || !canAssumePuid || isAssumedByMe"
                        @click="assumePuid"
                      >
                        Assume PUID
                      </v-btn>

                      <v-btn
                        color="error"
                        outlined
                        block
                        class="mb-4"
                        :loading="loading"
                        :disabled="loading || !canAssumePuid || !isAssumedByMe"
                        @click="releaseAssumption"
                      >
                        Release Assumption
                      </v-btn>
                    </v-col>

                    <v-col cols="12">
                      <v-autocomplete
                        outlined
                        dense
                        hide-details
                        label="Reassign to Staff"
                        :items="staffOptions"
                        item-text="text"
                        item-value="uid"
                        v-model="reassignToUid"
                        :disabled="loading || !canReassignPuid"
                      />

                      <v-btn
                        color="primary"
                        depressed
                        block
                        class="mt-3"
                        :loading="loading"
                        :disabled="
                          loading || !canReassignPuid || !reassignToUid
                        "
                        @click="reassignPuid"
                      >
                        Reassign PUID
                      </v-btn>

                      <p class="caption mt-2 mb-0" style="opacity: 0.8;">
                        Reassign will set the new owner and clear any
                        assumption.
                      </p>
                    </v-col>
                  </v-row>
                </div>

                <!-- Escalation (stacked in the same right lane) -->
                <div v-if="canEscalateHere || isEscalated" class="mt-8">
                  <v-divider class="my-4" />

                  <h3 class="mb-2 title">Escalation</h3>

                  <v-alert
                    v-if="isEscalated"
                    dense
                    outlined
                    type="warning"
                    class="mb-3"
                  >
                    <div class="d-flex flex-column">
                      <div>
                        <b>Escalated to:</b>
                        {{
                          escalationSupervisorName ||
                            escalationSupervisorUid ||
                            "—"
                        }}
                      </div>
                      <div v-if="escalatedAtDisplay">
                        <b>Escalated at:</b> {{ escalatedAtDisplay }}
                      </div>
                      <div v-if="escalatedByName">
                        <b>Escalated by:</b> {{ escalatedByName }}
                      </div>
                      <div v-if="userAccount && userAccount.isFrozen">
                        <b>Status:</b> Frozen for agent edits
                      </div>

                      <!-- ✅ show formatted notes -->
                      <div v-if="escalationNotesHtmlDisplay" class="mt-2">
                        <b>Notes:</b>
                        <div v-html="escalationNotesHtmlDisplay"></div>
                      </div>
                    </div>
                  </v-alert>

                  <v-alert v-else dense outlined type="info" class="mb-3">
                    Not escalated.
                  </v-alert>

                  <v-btn
                    v-if="canEscalateHere && !isEscalated"
                    color="yellow darken-2"
                    depressed
                    block
                    class="mb-2 black--text"
                    @click="openEscalateDialog"
                    :disabled="loading"
                    :loading="loading"
                  >
                    <v-icon left color="black">mdi-chevron-triple-up</v-icon>
                    Escalate to Supervisor
                  </v-btn>

                  <v-btn
                    v-if="canClearEscalation && isEscalated"
                    color="primary"
                    outlined
                    block
                    class="mb-2"
                    @click="clearEscalation"
                    :disabled="loading"
                    :loading="loading"
                  >
                    Return to Agent / Clear Escalation
                  </v-btn>

                  <!-- Escalate dialog -->
                  <v-dialog v-model="escalateDialog" max-width="850">
                    <v-card>
                      <v-toolbar dense flat color="yellow darken-2">
                        <v-toolbar-title class="black--text">
                          Escalate to Management
                        </v-toolbar-title>
                        <v-spacer />
                        <v-btn icon @click="escalateDialog = false">
                          <v-icon color="black">mdi-close</v-icon>
                        </v-btn>
                      </v-toolbar>

                      <v-card-text class="pt-6">
                        <v-alert dense outlined type="info" class="mb-4">
                          You are escalating this message to management. Please
                          select a supervisor.
                        </v-alert>

                        <v-autocomplete
                          outlined
                          dense
                          label="Supervisor"
                          :items="supervisorOptions"
                          item-text="text"
                          item-value="uid"
                          v-model="selectedSupervisorUid"
                          :disabled="loading"
                          :rules="[(v) => !!v || 'Required']"
                        />

                        <div class="mt-4">
                          <div class="mb-2 font-weight-medium">
                            Notes for supervisor (formatted)
                          </div>
                          <quill-editor
                            v-model="escalationNotesHtml"
                            class="custom-quill-editor"
                            :disabled="loading"
                          />
                        </div>
                      </v-card-text>

                      <v-card-actions class="px-6 pb-6">
                        <v-btn
                          text
                          @click="escalateDialog = false"
                          :disabled="loading"
                        >
                          Cancel
                        </v-btn>
                        <v-spacer />
                        <v-btn
                          color="primary"
                          depressed
                          @click="submitEscalation"
                          :loading="loading"
                          :disabled="loading || !selectedSupervisorUid"
                        >
                          Escalate
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                  <!-- /Escalate dialog -->
                </div>
              </v-col>

              <!-- adminNotes + signins (kept as-is) -->
              <v-col cols="12" md="4">
                <v-row>
                  <!-- adminNotes -->
                  <v-col cols="12">
                    <h3 class="mb-3 title">Admin Notes</h3>
                    <v-textarea
                      outlined
                      label="Admin Notes (not seen by traveler)"
                      hide-details
                      dense
                      v-model="userAccount.adminNotes"
                    ></v-textarea>
                    <div class="d-flex">
                      <v-spacer />
                      <v-btn
                        class="ml-auto mt-4"
                        color="primary"
                        small
                        depressed
                        @click="updateAdminNotes"
                        >Add/Update Admin Notes</v-btn
                      >
                    </div>
                  </v-col>

                  <!-- traveler sign ins -->
                  <v-col cols="12" class="my-6" v-if="signIns.length">
                    <v-divider />
                  </v-col>

                  <v-col cols="12" v-if="signIns.length">
                    <p class="caption">
                      IP address recording began on 3/20/2023
                    </p>
                    <h3 class="mb-3 title">Sign Ins</h3>
                    <div
                      style="max-height: 300px; overflow-y: auto; border: 1px solid #ccc; border-radius: 4px; padding: 8px;"
                    >
                      <ul>
                        <li v-for="(item, index) in signIns" :key="index">
                          <span class="mr-6">{{ formatDate(item.date) }}</span>
                          <span>{{ item.ipAddress }}</span>
                        </li>
                      </ul>
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <!-- row 1 -->

    <!-- adding message -->
    <v-fade-transition>
      <v-row v-if="addingMessage">
        <v-col cols="12">
          <v-card outlined :loading="loading">
            <v-card-text class="text--primary">
              <v-row>
                <v-col cols="12" md="6">
                  <div class="canned-header">
                    <h3 class="mb-4">Use Canned Response</h3>
                    <v-dialog v-model="dialogMessage" max-width="500px">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          small
                          color="primary"
                          class="mb-2"
                          v-bind="attrs"
                          v-on="on"
                          @click="messageItem.isEditing = false"
                          :loading="loading2"
                          :disabled="loading2"
                        >
                          Add New Message
                        </v-btn>
                      </template>
                      <v-card v-if="dialogMessage">
                        <v-form ref="form" @submit.prevent="saveCannedMessage">
                          <v-card-title>
                            <span class="text-h5">{{
                              messageItem.isEditing
                                ? "Edit Message"
                                : "Add Message"
                            }}</span>
                          </v-card-title>

                          <v-card-text>
                            <v-container>
                              <v-text-field
                                v-model="messageItem.text"
                                label="Title"
                                :rules="[(v) => !!v || 'Field Required']"
                                :loading="loading2"
                                :disabled="loading2"
                              ></v-text-field>

                              <v-textarea
                                v-model="messageItem.value"
                                label="Message"
                                :rules="[(v) => !!v || 'Field Required']"
                                :loading="loading2"
                                :disabled="loading2"
                              ></v-textarea>
                              <p class="hint-paragraph">
                                Hint : Please use these variables (eventName),
                                (numberNights), (travelByDate), (firstName),
                                (destination), (email), (packageID),
                                (packageCode), (nextYear), (expDate) to insert
                                the information dynamically.
                              </p>
                            </v-container>
                          </v-card-text>

                          <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn
                              color="blue darken-1"
                              text
                              @click="dialogMessage = false"
                              :loading="loading2"
                              :disabled="loading2"
                            >
                              Cancel
                            </v-btn>
                            <v-btn
                              color="blue darken-1"
                              text
                              @click="saveCannedMessage"
                              :loading="loading2"
                              :disabled="loading2"
                            >
                              Save
                            </v-btn>
                          </v-card-actions>
                        </v-form>
                      </v-card>
                    </v-dialog>
                  </div>
                  <v-autocomplete
                    outlined
                    v-model.trim="cannedResponse"
                    :items="cannedResponses"
                    dense
                    @change="insertCannedResponse"
                    label="Type/Select Canned Response"
                  >
                    <template #item="{ item }">
                      <div class="option-container">
                        <span>{{ item.text }}</span>
                        <div class="actions-canned">
                          <v-btn
                            icon
                            fab
                            x-small
                            @click="editCannedResponse(item, $event)"
                          >
                            <v-icon>mdi-pencil</v-icon>
                          </v-btn>
                          <v-btn
                            icon
                            fab
                            x-small
                            @click="deleteCannedResponse(item, $event)"
                          >
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </div>
                      </div>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-dialog v-model="dialogDelete" max-width="500px">
                  <v-card>
                    <v-card-title class="text-h5"
                      >Are you sure you want to delete this
                      message?</v-card-title
                    >
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn
                        color="blue darken-1"
                        text
                        @click="dialogDelete = false"
                        :loading="loading2"
                        :disabled="loading2"
                        >Cancel</v-btn
                      >
                      <v-btn
                        color="blue darken-1"
                        text
                        @click="deleteItemConfirm"
                        :loading="loading2"
                        :disabled="loading2"
                        >OK</v-btn
                      >
                      <v-spacer></v-spacer>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
                <v-col cols="12" md="6">
                  <h3 class="mb-4">Add Message</h3>
                  <v-form ref="newMessageForm">
                    <quill-editor
                      v-model="message.message"
                      class="custom-quill-editor"
                      :loading="loading"
                      :disabled="
                        loading || isFrozenForMe || isLockedBySomeoneElse
                      "
                    ></quill-editor>
                  </v-form>
                  <div class="d-flex mt-2">
                    <v-btn @click="unlockMessage" outlined color="error">
                      cancel / unlock
                    </v-btn>
                    <v-spacer />
                    <v-btn
                      color="primary"
                      depressed
                      @click="sendNewMessage"
                      :disabled="
                        loading || isFrozenForMe || isLockedBySomeoneElse
                      "
                    >
                      Send Message
                    </v-btn>
                  </div>

                  <v-alert
                    v-if="isFrozenForMe"
                    dense
                    outlined
                    type="warning"
                    class="mt-3"
                  >
                    This account is escalated. Only the assigned supervisor can
                    take actions right now.
                  </v-alert>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-fade-transition>
    <!-- adding message -->
    <v-alert
      v-if="isLockedBySomeoneElse"
      dense
      outlined
      type="warning"
      class="mb-3"
    >
      This message is currently in progress by
      <strong>{{ lockedByDisplay }}</strong
      >. You can view the account, but should not respond until the lock expires
      or is released.
    </v-alert>

    <v-alert v-if="isLockedByMe" dense outlined type="info" class="mb-3">
      You currently have this message locked.
    </v-alert>
    <!-- timeline -->
    <v-row>
      <v-col cols="12">
        <v-card outlined>
          <v-card-actions>
            <v-spacer />
            <v-btn
              v-if="isLockedBySomeoneElse && canManagePuid"
              small
              outlined
              color="warning"
              @click="unlockMessage"
            >
              Force Unlock
            </v-btn>
            <v-btn
              v-if="!addingMessage && timeline && timeline.length"
              color="primary"
              outlined
              @click="markAllMessagesRead"
              :disabled="loading || isFrozenForMe || isLockedBySomeoneElse"
            >
              Mark All Messages Read
            </v-btn>

            <v-btn
              v-if="!addingMessage"
              color="primary"
              depressed
              @click="lockMessage"
              :disabled="loading || isFrozenForMe || isLockedBySomeoneElse"
            >
              Start Message / Lock
            </v-btn>
          </v-card-actions>

          <v-card-text class="text--primary">
            <h3 class="title text-center mb-4">Timeline</h3>

            <v-timeline :dense="$vuetify.breakpoint.smAndDown">
              <v-timeline-item
                v-for="(item, index) in timeline"
                :key="index"
                :color="item.color"
              >
                <!-- initials in bubble (falls back to icon) -->
                <template v-slot:icon>
                  <div class="tl-icon">
                    <span v-if="getTimelineInitials(item)" class="tl-initials">
                      {{ getTimelineInitials(item) }}
                    </span>
                    <v-icon v-else small color="white">{{ item.icon }}</v-icon>
                  </div>
                </template>

                <v-card
                  elevation="1"
                  :outlined="$vuetify.theme.dark ? true : false"
                  :color="$vuetify.theme.dark ? '' : 'grey lighten-4'"
                >
                  <v-card-title
                    class="d-flex align-center justify-space-between"
                  >
                    <div class="d-flex align-center" style="gap: 10px;">
                      <div class="headline">{{ item.title }}</div>

                      <!-- sent-by label -->
                      <v-chip
                        v-if="getTimelineAgentLabel(item)"
                        small
                        class="tl-agent-chip"
                      >
                        {{ getTimelineAgentLabel(item) }}
                      </v-chip>
                    </div>

                    <!-- 24hr timestamp -->
                    <v-chip small class="tl-time-chip">
                      {{ getTimelineDateTimeDisplay(item) }}
                    </v-chip>
                  </v-card-title>

                  <v-card-text class="text--primary">
                    <p v-if="item.message" v-html="item.message"></p>
                  </v-card-text>

                  <v-card-actions
                    v-if="(item.message || '').includes('purchase')"
                    class="d-none"
                  >
                    <v-btn
                      text
                      :loading="loading"
                      :disabled="loading"
                      color="error"
                      class="ml-auto mt-4 d-block"
                    >
                      Email Your Travel Details to Yourself
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-timeline-item>
            </v-timeline>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <!-- timeline -->

    <!-- travelByDate dialog -->
    <v-dialog v-model="travelByDateDialog" width="290px" persistent>
      <v-card :loading="loading" :disabled="loading">
        <v-date-picker scrollable v-model.trim="eventReportWinner.travelByDate">
        </v-date-picker>
        <v-card-actions>
          <v-btn
            text
            @click="
              travelByDateDialog = false;
              eventReportWinner.travelByDate = eventReport.travelByDate;
            "
            >Cancel</v-btn
          >
          <v-spacer />
          <v-btn
            @click="updateTravelByDate"
            color="primary"
            depressed
            :disabled="loading || isFrozenForMe || isLockedBySomeoneElse"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- travelByDate dialog -->
  </v-container>
</template>

<script>
import { portalsApp } from "@/firebase";
import firebase from "@/plugins/firebase";
import axios from "axios";
import formatDate from "@/mixins/formatDate";
import moment from "moment";
import copyText from "@/mixins/copyText";
import formatPhone from "@/mixins/formatPhone";
import { can } from "@/services/auth";

export default {
  mixins: [formatDate, copyText],
  firestore: {
    retailPackages: portalsApp.firestore().collection("retailPackages"),
  },
  async mounted() {
    await this.$store.dispatch("setLoading", true);
    const cannedMessage = portalsApp.firestore().collection("cannedMessage");
    // bind userAccount
    await this.$bind(
      "userAccount",
      portalsApp
        .firestore()
        .collection("userAccounts")
        .doc(this.$route.params.id),
    );

    // bind eventReport
    await this.$bind(
      "eventReport",
      portalsApp
        .firestore()
        .collection("eventReports")
        .doc(this.userAccount.eventReportID),
    );

    // bind messages
    await this.$bind("cannedDbMessages", cannedMessage);

    const mps = portalsApp
      .firestore()
      .collection("mps")
      .where("puid", "==", this.userAccount.packageID);
    // bind mps
    await this.$bind("mps", mps);

    // filter eventReport winner
    this.eventReportWinner = this.eventReport.winners.filter(
      (w) => w.packageID == this.userAccount.packageID,
    )[0];

    await this.$store.dispatch("setLoading", false);
    await this.$bind("staffList", portalsApp.firestore().collection("staff"));
  },
  data: () => ({
    loading2: false,
    cannedDbMessages: [],
    dialogDelete: false,
    dialogMessage: false,
    dialog: false,
    messageItem: {
      isEditing: false,
      text: "",
      value: "",
    },
    emailSaving: false,
    email: "",
    oldEmail: "",
    emailRules: [
      (v) => !!v || "Email is required",
      (v) => /.+@.+\..+/.test(v) || "Email must be valid",
    ],
    travelByDateDialog: false,
    cannedResponse: "",
    mps: "",
    addingMessage: false,
    message: {
      adminActionRequired: false,
      archived: false,
      dateCreated: new Date().toISOString().substring(0, 10),
      from: "admin",
      message: "",
      readByAdmin: true,
      repliedToByAdmin: true,
      senderActionRequired: false,
      title: "We sent you a message",
      to: "",
      icon: "mdi-message-reply-text-outline",
      color: "primary",
    },
    userAccount: {
      termsAndConditions: {
        agreed: "",
        title: "",
        content: "",
        id: "",
      },
    },
    eventReport: "",
    eventReportWinner: "",
    retailPackages: [],
    staffList: [],
    reassignToUid: null,

    // escalation UI state
    escalateDialog: false,
    selectedSupervisorUid: "",
    escalationNotesHtml: "",
  }),
  watch: {
    dialog(newValue) {
      if (newValue) this.email = "";
    },
    dialogMessage(newValue) {
      if (!newValue)
        this.messageItem = { isEditing: false, text: "", value: "" };
    },
  },
  computed: {
    isEmailValid() {
      return this.emailRules.every((rule) => rule(this.email) === true);
    },
    loading() {
      return this.$store.state.loading;
    },
    timeline() {
      if (!this.userAccount) return;
      if (!this.userAccount.timeline) return;
      return this.userAccount.timeline.reverse();
    },
    nextYear() {
      let todayThisYear = moment().startOf("year");
      let nextYear = todayThisYear.add("1", "year").format("YYYY");
      return nextYear;
    },
    cannedResponses() {
      return this.constructMessages(this.cannedDbMessages);
    },
    signIns() {
      if (!this.userAccount) return [];
      if (!this.userAccount.signIns) return [];
      return this.userAccount.signIns;
    },
    currentUser() {
      return this.$store.state.user || null;
    },
    currentUid() {
      return this.currentUser && this.currentUser.uid
        ? this.currentUser.uid
        : "";
    },
    currentName() {
      const s =
        this.currentUser && this.currentUser.staff
          ? this.currentUser.staff
          : {};
      return (
        (s.fullName && String(s.fullName).trim()) ||
        (s.displayName && String(s.displayName).trim()) ||
        (s.name && String(s.name).trim()) ||
        (
          (this.currentUser.firstName || "") +
          " " +
          (this.currentUser.lastName || "")
        ).trim() ||
        ""
      );
    },

    canAssumePuid() {
      return can(this.currentUser, this.$store, "assumePuid");
    },
    canReassignPuid() {
      return can(this.currentUser, this.$store, "reassignPuid");
    },
    canManagePuid() {
      return this.canAssumePuid || this.canReassignPuid;
    },

    // permission used for escalation button (add this to permissions.js later if missing)
    canEscalateHere() {
      // allow managers/admin/booking with explicit permission
      return (
        can(this.currentUser, this.$store, "escalateMessages") ||
        this.canManagePuid
      );
    },

    // who can clear escalation
    canClearEscalation() {
      // supervisor or management can clear
      if (!this.isEscalated) return false;
      return this.isSupervisorForThis || this.canManagePuid;
    },

    isAssumedByMe() {
      if (!this.userAccount) return false;
      return (
        !!this.userAccount.assumedByUid &&
        this.userAccount.assumedByUid === this.currentUid
      );
    },

    staffOptions() {
      if (!this.staffList || !this.staffList.length) return [];
      return this.staffList
        .filter(function(s) {
          return s && s.uid;
        })
        .map(function(s) {
          var full =
            (s.fullName && String(s.fullName).trim()) ||
            (s.displayName && String(s.displayName).trim()) ||
            (s.name && String(s.name).trim()) ||
            "";

          if (!full) {
            var first = (s.firstName || "").trim();
            var last = (s.lastName || "").trim();
            full = (first + " " + last).trim();
          }

          return {
            uid: s.uid,
            text: full || s.email || s.uid,
            email: s.email || "",
          };
        })
        .sort(function(a, b) {
          return String(a.text).localeCompare(String(b.text));
        });
    },

    // Escalation state
    escalation() {
      return (this.userAccount && this.userAccount.escalation) || {};
    },
    isEscalated() {
      return !!(this.escalation && this.escalation.isEscalated);
    },
    escalationSupervisorUid() {
      return (this.escalation && this.escalation.supervisorUid) || "";
    },
    escalationSupervisorName() {
      return (this.escalation && this.escalation.supervisorName) || "";
    },
    escalatedByName() {
      return (this.escalation && this.escalation.escalatedByName) || "";
    },
    escalationNotesHtmlDisplay() {
      const html = (this.escalation && this.escalation.notesHtml) || "";
      return html && String(html).trim().length ? html : "";
    },
    isSupervisorForThis() {
      return (
        !!this.escalationSupervisorUid &&
        this.escalationSupervisorUid === this.currentUid
      );
    },
    escalatedAtDisplay() {
      const val = this.escalation && this.escalation.escalatedAt;
      if (!val) return "";
      try {
        const d =
          typeof val.toDate === "function" ? val.toDate() : new Date(val);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleString();
      } catch (e) {
        return "";
      }
    },

    // used to disable actions for non-supervisors when escalated
    isFrozenForMe() {
      if (!this.isEscalated) return false;
      // supervisor and management can still act
      if (this.isSupervisorForThis) return false;
      if (this.canManagePuid) return false;
      // everyone else frozen
      return true;
    },

    // supervisor dropdown options (TEMP: uses staff list, but filter to just Ashley/Greg via uid list later)
    supervisorOptions() {
      // TODO: later: hardcode allowed supervisor UIDs (Ashley, Greg)
      // For now: if you have "role" field, filter to Management/Owner
      const list = this.staffList || [];
      return list
        .filter((s) => {
          if (!s || !s.uid) return false;
          const role = (s.role || "").toLowerCase();
          return (
            role.includes("management") ||
            role.includes("superadmin") ||
            role.includes("admin")
          );
        })
        .map((s) => ({
          uid: s.uid,
          text: s.fullName || s.email || s.uid,
        }))
        .sort((a, b) => String(a.text).localeCompare(String(b.text)));
    },
    isLocked() {
      if (!this.userAccount || !this.userAccount.locked) return false;

      const lockedAt = this.toMillis(this.userAccount.lockedAt);
      if (!lockedAt) return false;

      const thirtyMinutes = 30 * 60 * 1000;
      return new Date().getTime() - lockedAt < thirtyMinutes;
    },

    isLockedByMe() {
      return (
        this.isLocked &&
        this.userAccount.lockedBy &&
        this.userAccount.lockedBy === this.currentUid
      );
    },

    isLockedBySomeoneElse() {
      return this.isLocked && !this.isLockedByMe;
    },

    lockedByDisplay() {
      return this.userAccount.lockedByName || this.userAccount.lockedBy || "";
    },
  },
  methods: {
    openEscalateDialog() {
      this.selectedSupervisorUid = "";
      this.escalationNotesHtml = "";
      this.escalateDialog = true;
    },

    async submitEscalation() {
      if (!this.canEscalateHere) return;
      if (!this.userAccount || !this.userAccount.id) return;
      if (!this.selectedSupervisorUid) return;

      await this.$store.dispatch("setLoading", true);

      try {
        const sup = (this.supervisorOptions || []).find(
          (s) => s.uid === this.selectedSupervisorUid,
        );

        const escalationPatch = {
          escalation: {
            isEscalated: true,
            status: "open",
            supervisorUid: this.selectedSupervisorUid,
            supervisorName: sup && sup.text ? sup.text : null,
            escalatedByUid: this.currentUid || null,
            escalatedByName: this.currentName || null,
            escalatedAt: firebase.firestore.FieldValue.serverTimestamp(),
            notesHtml: this.escalationNotesHtml || "",
          },
          isFrozen: true,
        };

        await portalsApp
          .firestore()
          .collection("userAccounts")
          .doc(this.userAccount.id)
          .update(escalationPatch);

        this.$toast && this.$toast.success
          ? this.$toast.success("Escalated to supervisor", { timeout: 2000 })
          : null;

        this.escalateDialog = false;
      } catch (e) {
        console.log(e);
        this.$toast && this.$toast.error
          ? this.$toast.error(e && e.message ? e.message : "Escalation failed")
          : null;
      } finally {
        await this.$store.dispatch("setLoading", false);
      }
    },

    async clearEscalation() {
      if (!this.canClearEscalation) return;
      if (!this.userAccount || !this.userAccount.id) return;

      await this.$store.dispatch("setLoading", true);

      try {
        const patch = {
          escalation: {
            isEscalated: false,
            status: "returned",
            supervisorUid: null,
            supervisorName: null,
            escalatedByUid: null,
            escalatedByName: null,
            escalatedAt: null,
            notesHtml: "",
            returnedAt: firebase.firestore.FieldValue.serverTimestamp(),
            returnedByUid: this.currentUid || null,
            returnedByName: this.currentName || null,
          },
          isFrozen: false,
        };

        await portalsApp
          .firestore()
          .collection("userAccounts")
          .doc(this.userAccount.id)
          .update(patch);

        this.$toast && this.$toast.success
          ? this.$toast.success("Escalation cleared", { timeout: 2000 })
          : null;
      } catch (e) {
        console.log(e);
        this.$toast && this.$toast.error
          ? this.$toast.error(
              e && e.message ? e.message : "Failed to clear escalation",
            )
          : null;
      } finally {
        await this.$store.dispatch("setLoading", false);
      }
    },

    constructMessages(messages) {
      if (!messages) return [];

      const replaceFrom = [
        {
          old: "(eventDate)",
          new: this.formatDate(this.eventReport.eventDate),
        },
        {
          old: "(numberNights)",
          new: this.eventReportWinner.numberNights,
        },
        {
          old: "(travelByDate)",
          new: this.formatDate(this.eventReportWinner.travelByDate),
        },
        { old: "(firstName)", new: this.userAccount.firstName },
        { old: "(destination)", new: this.eventReportWinner.destination },
        { old: "(email)", new: this.userAccount.email },
        { old: "(packageID)", new: this.eventReportWinner.packageID },
        { old: "(packageCode)", new: this.eventReportWinner.packageCode },
        { old: "(nextYear)", new: this.nextYear },
        {
          old: "(expDate)",
          new:
            this.mps.length && this.mps[0].originalExpiryDate
              ? this.mps[0].originalExpiryDate
              : " ",
        },
      ];
      const updatedMessages = messages.map((el) => {
        let msg = { ...el };
        replaceFrom.map((textFrom) => {
          msg.text = msg.text.replaceAll(textFrom.old, textFrom.new).trim();
          msg.value = msg.value.replaceAll(textFrom.old, textFrom.new).trim();
        });
        return msg;
      });
      return updatedMessages;
    },

    async deleteItemConfirm() {
      this.loading2 = true;
      await portalsApp
        .firestore()
        .collection("cannedMessage")
        .doc(this.messageItem.id)
        .delete();
      this.dialogDelete = false;
      this.loading2 = false;
    },
    async saveCannedMessage() {
      if (!this.$refs.form.validate()) {
        await this.$store.dispatch("setLoading", false);
        await this.$store.dispatch("setSnackbar", {
          display: true,
          color: "error",
          text: "Please check form for errors",
          timeout: 10000,
        });
        return;
      }
      this.loading2 = true;

      let data = this.messageItem;
      if (data.isEditing) {
        delete data.isEditing;
        await portalsApp
          .firestore()
          .collection("cannedMessage")
          .doc(data.id)
          .update(data);

        this.loading2 = false;
        this.dialogMessage = false;
      } else {
        delete data.isEditing;
        await portalsApp
          .firestore()
          .collection("cannedMessage")
          .add(data);

        this.loading2 = false;
        this.dialogMessage = false;
      }
    },
    editCannedResponse(item, event) {
      event.stopPropagation();
      this.messageItem = this.cannedDbMessages.find((el) => el.id === item.id);
      this.messageItem.isEditing = true;
      this.dialogMessage = true;
    },
    deleteCannedResponse(item, event) {
      event.stopPropagation();
      this.messageItem = this.cannedDbMessages.find((el) => el.id === item.id);
      this.dialogDelete = true;
    },

    async saveEmail() {
      this.oldEmail = this.userAccount.email;
      this.emailSaving = true;
      await this.$store.dispatch("setLoading", true);

      let users = portalsApp
        .firestore()
        .collection("userAccounts")
        .where("email", "==", this.email);
      let usersData = await users.get();
      if (usersData.docs.length) {
        this.loading = false;
        this.dialog = false;
        this.email = "";
        this.emailSaving = false;
        await this.$store.dispatch("setLoading", false);
        await this.$store.dispatch("setSnackbar", {
          display: true,
          color: "error",
          text: "Email already exist",
          timeout: 5000,
        });
      } else {
        const updateUserEmailData = {
          uid: this.$route.params.id,
          email: this.email,
        };

        await axios
          .post(
            "https://api.silentauctionvacations.com/updateEmail",
            updateUserEmailData,
          )
          .then((res) => {
            this.updateEmailToUserAccount();
            this.sendMailToUser();
          })
          .catch((err) => {
            console.log(err);
            this.dialog = false;
            this.emailSaving = false;
            this.$store.dispatch("setLoading", false);
          });
      }
    },
    sendMailToUser() {
      axios
        .post("/api/send-email-updation", { email: this.email })
        .then((res) => {})
        .catch((error) => {
          console.log(error);
        });
    },
    async updateEmailToUserAccount() {
      const user = await portalsApp
        .firestore()
        .collection("userAccounts")
        .doc(this.$route.params.id);

      this.userAccount.email = this.email;
      this.userAccount.oldEmail = this.oldEmail;

      await user
        .update(this.userAccount)
        .then((res) => {
          this.$toast.success(`Email Updated successfully!`, {
            timeout: 2000,
          });
          this.dialog = false;
          this.emailSaving = false;
          this.$store.dispatch("setLoading", false);
        })
        .catch((error) => {
          this.$toast.error(`Something went wrong!`, {
            timeout: 2000,
          });
          this.emailSaving = false;
          this.$store.dispatch("setLoading", false);
        });
    },

    async updateTravelByDate() {
      if (this.isFrozenForMe) return;

      await this.$store.dispatch("setLoading", true);

      let eventReportDocRef = portalsApp
        .firestore()
        .collection("eventReports")
        .doc(this.userAccount.eventReportID);
      let eventReportDoc = await eventReportDocRef.get();
      let eventReportData = eventReportDoc.data();

      let winners = eventReportData.winners;
      winners.forEach((i) => {
        if (i.packageID == this.userAccount.packageID) {
          i.travelByDate = this.eventReportWinner.travelByDate;
        }
      });

      await eventReportDocRef.update({ winners });

      await this.$store.dispatch("setLoading", false);
      this.travelByDateDialog = false;

      await this.$store.dispatch("setSnackbar", {
        display: true,
        color: "success",
        text: "Travel by date updated",
        timeout: 5000,
      });
    },

    async markAllMessagesRead() {
      if (this.isFrozenForMe) return;

      await this.$store.dispatch("setLoading", true);

      let userAccountRef = portalsApp
        .firestore()
        .collection("userAccounts")
        .doc(this.userAccount.id);

      let userAccountData = await userAccountRef.get();
      userAccountData = userAccountData.data();

      let timeline = userAccountData.timeline;
      timeline.forEach((i) => {
        i.readByAdmin = true;
        i.adminActionRequired = false;
        i.repliedToByAdmin = true;
      });

      await userAccountRef.update({
        timeline: timeline,
      });

      await this.$store.dispatch("setSnackbar", {
        display: true,
        color: "success",
        text: "All messages marked as read",
        timeout: 5000,
      });

      await this.$store.dispatch("setLoading", false);
      this.addingMessage = false;
      return this.$router.push("/messages");
    },

    async updateAdminNotes() {
      if (!this.userAccount.adminNotes || !this.userAccount.adminNotes.length)
        return;

      await this.$store.dispatch("setLoading", true);

      let userAccount = portalsApp
        .firestore()
        .collection("userAccounts")
        .doc(this.userAccount.id);

      await userAccount.update({
        adminNotes: this.userAccount.adminNotes,
      });

      await this.$store.dispatch("setLoading", false);

      await this.$store.dispatch("setSnackbar", {
        display: true,
        color: "success",
        text: "Admin notes updated successfully",
        timeout: 5000,
      });
    },

    async sendNewMessage() {
      if (this.isLockedBySomeoneElse) {
        await this.$store.dispatch("setSnackbar", {
          display: true,
          color: "error",
          text: "This message is currently locked by another team member.",
          timeout: 8000,
        });
        return;
      }
      if (this.isFrozenForMe) return;

      if (!this.$refs.newMessageForm.validate()) return;
      if (!this.message.message || !this.message.message.length) return;

      await this.$store.dispatch("setLoading", true);

      try {
        // Build a fresh timeline entry every send (so dateCreated isn't stale)
        const staff = (this.currentUser && this.currentUser.staff) || {};
        const sentByName =
          (staff.fullName && String(staff.fullName).trim()) ||
          (staff.name && String(staff.name).trim()) ||
          (staff.displayName && String(staff.displayName).trim()) ||
          "";

        const sentByRole = (staff.role && String(staff.role).trim()) || "";
        const sentByTitle = (staff.title && String(staff.title).trim()) || "";

        const entry = {
          adminActionRequired: false,
          archived: false,
          color: this.message.color || "primary",
          // keep existing field for backwards compatibility
          dateCreated: new Date().toISOString().substring(0, 10),
          // real timestamp for 24hr display
          createdAt: firebase.firestore.Timestamp.now(),

          from: "admin",
          icon: this.message.icon || "mdi-message-reply-text-outline",
          message: this.message.message || "",
          readByAdmin: true,
          repliedToByAdmin: true,
          senderActionRequired: false,
          title: this.message.title || "We sent you a message",
          to: this.userAccount.email || "",

          // signature fields for initials + agent label
          sentByUid: (this.currentUser && this.currentUser.uid) || "",
          sentByName: sentByName,
          sentByRole: sentByRole,
          sentByTitle: sentByTitle,
        };

        if (this.cannedResponse && this.cannedResponse.includes("transfer")) {
          entry.senderActionRequired = true;
        }

        const userAccountRef = portalsApp
          .firestore()
          .collection("userAccounts")
          .doc(this.userAccount.id);

        await userAccountRef.update({
          timeline: firebase.firestore.FieldValue.arrayUnion(entry),
        });

        // mark everything read / no admin action required (your existing behavior)
        let snap = await userAccountRef.get();
        let data = snap.data() || {};
        let timeline = data.timeline || [];

        timeline.forEach((i) => {
          i.readByAdmin = true;
          i.adminActionRequired = false;
          i.repliedToByAdmin = true;
        });

        await userAccountRef.update({
          timeline,
          locked: false,
          lockedBy: "",
          lockedByName: "",
          lockedAt: null,
        });

        await axios.post("/api/new-dashboard-message", {
          email: this.userAccount.email,
          secondaryEmail: this.userAccount.secondaryEmail,
        });

        this.$refs.newMessageForm.reset();
        this.addingMessage = false;

        await this.$store.dispatch("setSnackbar", {
          display: true,
          color: "success",
          text: "Message sent successfully",
          timeout: 5000,
        });

        this.$router.push("/messages");
      } catch (e) {
        console.log(e);
        await this.$store.dispatch("setSnackbar", {
          display: true,
          color: "error",
          text: e && e.message ? e.message : "Failed to send message",
          timeout: 8000,
        });
      } finally {
        await this.$store.dispatch("setLoading", false);
      }
    },

    insertCannedResponse() {
      this.$refs.newMessageForm.reset();
      this.message.message = this.cannedResponse;
    },

    async updateAccessControls() {
      await this.$store.dispatch("setLoading", true);
      this.userAccount.dateUpdated = new Date().toISOString().substring(0, 10);
      await portalsApp
        .firestore()
        .collection("userAccounts")
        .doc(this.$route.params.id)
        .update(this.userAccount);
      await this.$store.dispatch("setLoading", false);
      await this.$store.dispatch("setSnackbar", {
        display: true,
        color: "success",
        text: "User updated successfully",
        timeout: 5000,
      });
    },

    async assumePuid() {
      if (!this.canAssumePuid) return;
      if (!this.userAccount || !this.userAccount.id) return;

      await this.$store.dispatch("setLoading", true);

      try {
        const docRef = portalsApp
          .firestore()
          .collection("userAccounts")
          .doc(this.userAccount.id);

        const first =
          (this.currentUser &&
            this.currentUser.staff &&
            this.currentUser.staff.firstName) ||
          (this.currentUser && this.currentUser.firstName) ||
          "";
        const last =
          (this.currentUser &&
            this.currentUser.staff &&
            this.currentUser.staff.lastName) ||
          (this.currentUser && this.currentUser.lastName) ||
          "";

        const assumedByName =
          (String(first) + " " + String(last)).trim() || null;

        const patch = {
          assumedByUid:
            this.currentUid ||
            (this.currentUser && this.currentUser.uid) ||
            null,
          assumedByName: assumedByName,
          assumedAt: firebase.firestore.FieldValue.serverTimestamp(),
        };

        await docRef.update(patch);
        await this.syncMpsOwnership(this.userAccount.packageID, patch);
        await this.syncBookingsOwnership(this.getAllConfirmationIds(), patch);

        await this.$store.dispatch("setSnackbar", {
          display: true,
          color: "success",
          text: "PUID assumed",
          timeout: 4000,
        });
      } finally {
        await this.$store.dispatch("setLoading", false);
      }
    },

    async releaseAssumption() {
      if (!this.canAssumePuid) return;
      if (!this.isAssumedByMe) return;
      if (!this.userAccount || !this.userAccount.id) return;

      await this.$store.dispatch("setLoading", true);

      try {
        const docRef = portalsApp
          .firestore()
          .collection("userAccounts")
          .doc(this.userAccount.id);

        const patch = {
          assumedByUid: null,
          assumedByName: null,
          assumedAt: null,
        };

        await docRef.update(patch);
        await this.syncMpsOwnership(this.userAccount.packageID, patch);
        await this.syncBookingsOwnership(this.getAllConfirmationIds(), patch);

        await this.$store.dispatch("setSnackbar", {
          display: true,
          color: "success",
          text: "Assumption released",
          timeout: 4000,
        });
      } finally {
        await this.$store.dispatch("setLoading", false);
      }
    },

    async reassignPuid() {
      if (!this.canReassignPuid) return;
      if (!this.reassignToUid) return;
      if (!this.userAccount || !this.userAccount.id) return;

      const staff = (this.staffOptions || []).find(
        (s) => s.uid === this.reassignToUid,
      );

      await this.$store.dispatch("setLoading", true);

      const docRef = portalsApp
        .firestore()
        .collection("userAccounts")
        .doc(this.userAccount.id);

      const assignedToName = staff && staff.text ? staff.text : null;

      const patch = {
        assignedToUid: this.reassignToUid,
        assignedToName: assignedToName,
        assignedAt: firebase.firestore.FieldValue.serverTimestamp(),

        assumedByUid: null,
        assumedByName: null,
        assumedAt: null,
      };

      await docRef.update(patch);
      await this.syncMpsOwnership(this.userAccount.packageID, patch);
      await this.syncBookingsOwnership(this.getAllConfirmationIds(), patch);

      await this.$store.dispatch("setLoading", false);

      this.reassignToUid = null;

      await this.$store.dispatch("setSnackbar", {
        display: true,
        color: "success",
        text: "PUID reassigned (and MPS updated)",
        timeout: 4000,
      });
    },

    async syncMpsOwnership(puid, patch) {
      if (!puid) return;

      const snap = await portalsApp
        .firestore()
        .collection("mps")
        .where("puid", "==", puid)
        .get();

      if (!snap || !snap.docs || !snap.docs.length) return;

      const batch = portalsApp.firestore().batch();

      snap.docs.forEach((doc) => {
        batch.update(doc.ref, patch);
      });

      await batch.commit();
    },

    async syncBookingsOwnership(confirmationIds, patch) {
      if (!confirmationIds || !confirmationIds.length) return;

      const CHUNK = 400;

      for (let i = 0; i < confirmationIds.length; i += CHUNK) {
        const chunk = confirmationIds.slice(i, i + CHUNK);
        const batch = portalsApp.firestore().batch();

        for (let j = 0; j < chunk.length; j++) {
          const id = chunk[j];
          const ref = portalsApp
            .firestore()
            .collection("bookingsConfirmations")
            .doc(id);
          const snap = await ref.get();
          if (snap.exists) batch.update(ref, patch);
        }

        await batch.commit();
      }
    },

    getAllConfirmationIds() {
      const list =
        (this.userAccount && this.userAccount.inventorySelections) || [];
      const map = {};

      list.forEach((id) => {
        if (!id) return;
        const clean = String(id).trim();
        if (clean) map[clean] = true;
      });

      return Object.keys(map);
    },

    toJsDate(val) {
      if (!val) return null;
      try {
        if (typeof val.toDate === "function") return val.toDate(); // Firestore Timestamp
        if (val instanceof Date) return val;
        const d = new Date(val);
        return isNaN(d.getTime()) ? null : d;
      } catch (e) {
        return null;
      }
    },

    formatDateTime24(d) {
      if (!d || isNaN(d.getTime())) return "";

      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York",
        weekday: "short",
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      // e.g. "Mon, Dec 08, 2025, 13:45"
      let str = formatter.format(d);

      // => "MON DEC 08 2025 13:45" then re-shape to "MON Dec 08, 2025 13:45"
      // We'll format it cleanly:
      // Remove commas then uppercase weekday only:
      str = str.replace(/,/g, "");
      // "Mon Dec 08 2025 13:45"
      const parts = str.split(" ");
      if (parts.length >= 5) {
        const wd = parts[0].toUpperCase();
        const mon = parts[1];
        const day = parts[2];
        const year = parts[3];
        const time = parts[4];
        return `${wd} ${mon} ${day}, ${year} ${time} ET`;
      }

      return str + " ET";
    },

    getTimelineDateTimeDisplay(item) {
      const d =
        this.toJsDate(item && item.createdAt) ||
        this.toJsDate(item && item.dateCreated) ||
        null;

      return this.formatDateTime24(d) || "—";
    },

    getTimelineInitials(item) {
      const name = (item && item.sentByName) || "";
      const parts = String(name)
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      if (parts.length === 1 && parts[0].length) {
        return parts[0][0].toUpperCase();
      }

      if ((item && item.from) === "admin") return "A";
      return "";
    },

    getTimelineAgentLabel(item) {
      const name = (item && item.sentByName) || "";
      const title = (item && (item.sentByTitle || item.sentByRole)) || "";

      const parts = String(name)
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      if (!parts.length) return "";

      const firstInitial = parts[0][0] ? parts[0][0].toUpperCase() : "";
      const last = parts.length > 1 ? parts[parts.length - 1] : parts[0];
      const lastNice = last ? last.charAt(0).toUpperCase() + last.slice(1) : "";

      const left =
        firstInitial && lastNice ? `${firstInitial}.${lastNice}` : "";
      const right = title ? String(title).trim() : "";

      if (left && right) return `${left} - ${right}`;
      return left || right || "";
    },
    toMillis(val) {
      if (!val) return 0;
      if (val && typeof val.toDate === "function") {
        return val.toDate().getTime() || 0;
      }

      const d = new Date(val);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    },
    async lockMessage() {
      if (!this.userAccount || !this.userAccount.id) return;

      await this.$store.dispatch("setLoading", true);

      try {
        await portalsApp
          .firestore()
          .collection("userAccounts")
          .doc(this.userAccount.id)
          .update({
            locked: true,
            lockedBy: this.currentUid || "",
            lockedByName: this.currentName || "",
            lockedAt: firebase.firestore.FieldValue.serverTimestamp(),
          });

        this.addingMessage = true;
      } finally {
        await this.$store.dispatch("setLoading", false);
      }
    },

    async unlockMessage() {
      if (!this.userAccount || !this.userAccount.id) return;

      await this.$store.dispatch("setLoading", true);

      try {
        await portalsApp
          .firestore()
          .collection("userAccounts")
          .doc(this.userAccount.id)
          .update({
            locked: false,
            lockedBy: "",
            lockedByName: "",
            lockedAt: null,
          });

        this.addingMessage = false;
      } finally {
        await this.$store.dispatch("setLoading", false);
      }
    },
  },
};
</script>

<style>
.option-container {
  display: flex;
  justify-content: space-between;
  width: -webkit-fill-available;
}

.option-container span {
  flex-grow: 1;
  margin-right: 10px;
}
.canned-header {
  display: flex;
  justify-content: space-between;
}
.hint-paragraph {
  font-size: smaller;
  line-height: initial;
  color: #595a5c;
}

.custom-quill-editor .ql-container {
  resize: vertical;
  overflow: auto;
  min-height: 150px;
  max-height: 600px;
}
.tl-agent-chip {
  background-color: #fff59d !important;
  color: #111827 !important;
  font-weight: 700;
  border: 1px solid #facc15 !important;
}
</style>
